import React, { PureComponent } from 'react';
import {
  SafeAreaView,
  StyleSheet, View, Text,
  Image,
} from 'react-native';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import { defaultScale } from '../../../../utils/scaling';
import { getNextStep, navigateToNextStep } from '../../../actions';
import { IAppStore } from '../../../../HomeWorkouts';
import { GoogleAnalytics } from '../../../../services/google-analytics';
import Theme from '../../../../theme';
import { UserDataConsumer } from '../../../../utils';
import HealthConnectImage from '../../../../assets/icons/health_connect_logo.png';
import { IConnectedAppOnboardingProps, navigateToNextOnboardingStep } from '../ConnectedAppEntryPointScreen';
import SupportedDevices from '../../../../assets/icons/connected-apps/supported-devices.svg';
import FooterButtons from '../shared/FooterButtons';
import MultiStyleText from '../../../../ui/components/MultiStyleText';
import ConnectedAppsManagerSingleton, { IAppForOnboarding } from '../../../../ConnectedApps';
import { connectToApp } from '../utils';
import { showMessage } from '../../../../ui/components/Toast';
import { Logger } from '../../../../utils/Logger';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingVertical: defaultScale(16),
    backgroundColor: Theme.WHITE,
  },
  headerContainer: {
    alignItems: 'center',
    marginTop: '10%',
    textAlign: 'center',
    flex: 1,
  },
  heading: {
    textAlign: 'center',
  },
  subHeader: {
    color: Theme.GREY_DARK_MEDIUM,
    marginBottom: defaultScale(20),
    paddingHorizontal: defaultScale(10),
    textAlign: 'center',
  },
});

interface IState {
  isLoading: boolean;
}

class EnableHealthConnect extends PureComponent<IConnectedAppOnboardingProps & WithTranslation, IState> {
  private readonly googleAnalytics: GoogleAnalytics;

  private readonly appForOnboarding: IAppForOnboarding = this.props.navigation.getParam('appForOnboarding');

  public constructor(props: IConnectedAppOnboardingProps & WithTranslation) {
    super(props);
    this.googleAnalytics = new GoogleAnalytics();
    this.state = {
      isLoading: false,
    };
  }

  public render() {
    const { t, navigation } = this.props;
    const appForOnboarding = navigation.getParam('appForOnboarding');

    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.headerContainer}>
          <MultiStyleText
            templateString={t('enableApp')}
            styleKeys={[
              {
                keyName: 'connectedApp',
                keyValue: appForOnboarding.app,
                style: Theme.textStyle.heading,
              },
            ]}
            normalStyle={{ ...Theme.textStyle.heading, ...styles.heading }}
          />
          <Image
            source={HealthConnectImage}
            style={{ width: defaultScale(100), height: defaultScale(100), marginTop: defaultScale(30) }}
          />
          <Text style={[Theme.textStyle.bodyCopyReg, styles.subHeader]}>
            {t('syncActivities')}
          </Text>
          <SupportedDevices width={defaultScale(300)} />
        </View>
        <FooterButtons
          primaryButtonText={t('enable')}
          secondaryButtonText={t('maybeLater')}
          onPrimaryButtonPress={() => this.enableHealthConnect()}
          onSecondaryButtonPress={() => this.proceedToNextStep()}
          isLoading={this.state.isLoading}
        />
      </SafeAreaView>
    );
  }

  private async enableHealthConnect() {
    this.setState({ isLoading: true });
    try {
      if (this.appForOnboarding.isAppInstalled) {
        const app = ConnectedAppsManagerSingleton.getAppByName(this.appForOnboarding.app);
        if (!app) {
          showMessage(`${this.appForOnboarding.app} failed to install`);
          return;
        }

        const result = await connectToApp(app);

        if (result === 'success') {
          this.googleAnalytics.logEventWithParamaters('sign_up_install_health_connect', { enabled: true });
          this.props.navigation.navigate('DoYouHaveATrackingDevice', { appForOnboarding: this.appForOnboarding });
        }
        this.setState({ isLoading: false });
      } else {
        this.props.navigation.navigate('InstallHealthConnect', { appForOnboarding: this.appForOnboarding });
        this.setState({ isLoading: false });
      }
      this.googleAnalytics.logEventWithParamaters('sign_up_enable_health_connect', { enabled: true });
    } catch (error) {
      Logger.notify(error);
      showMessage(`Failed to enable ${this.appForOnboarding.app}`);
      this.setState({ isLoading: false });
    }
  }

  private proceedToNextStep = () => {
    navigateToNextOnboardingStep(
      this.props,
      this.props.getNextStep,
      this.props.navigateToNextStep,
    );
    this.googleAnalytics.logEventWithParamaters('sign_up_enable_apple_health_proceed', { enabled: true });
  };
}

const mapStateToProps = (state: IAppStore) => ({
  enabledFeatures: state.auth.signup.enabledFeatures,
});

const mapDispatchToProps = {
  getNextStep,
  navigateToNextStep,
};

export default withTranslation(['auth', 'settings'])(
  connect(mapStateToProps, mapDispatchToProps)(UserDataConsumer(EnableHealthConnect)),
);
